import React from 'react';
import { Loader2 } from 'lucide-react';
import { Button as ShadButton, type ButtonProps as ShadButtonProps } from 'ui/shadcn/button';
import { cn } from 'lib/utils';

interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'size'> {
    variant?: 'primary' | 'secondary' | 'danger' | 'danger-outline' | 'ghost' | 'warning' | 'outline';
    size?: 'small' | 'medium' | 'large';
    fullWidth?: boolean;
    loading?: boolean;
    icon?: React.ReactNode;
}

const variantMap: Record<NonNullable<ButtonProps['variant']>, ShadButtonProps['variant']> = {
    primary: 'default',
    secondary: 'secondary',
    danger: 'destructive',
    'danger-outline': 'destructive-outline',
    ghost: 'ghost',
    warning: 'warning',
    outline: 'outline',
};

const sizeMap: Record<NonNullable<ButtonProps['size']>, ShadButtonProps['size']> = {
    small: 'sm',
    medium: 'default',
    large: 'lg',
};

const Button: React.FC<ButtonProps> = ({
    variant = 'primary',
    size = 'medium',
    fullWidth = false,
    loading = false,
    icon,
    children,
    className = '',
    disabled,
    ...props
}) => {
    return (
        <ShadButton
            variant={variantMap[variant]}
            size={sizeMap[size]}
            disabled={disabled || loading}
            className={cn(fullWidth && 'w-full', className)}
            {...props}
        >
            {loading ? (
                <>
                    <Loader2 className="animate-spin" />
                    Loading...
                </>
            ) : (
                <>
                    {icon}
                    {children}
                </>
            )}
        </ShadButton>
    );
};

export default Button;
